import { useEffect, useRef, useState } from 'react';
import { css } from 'styled-system/css';

const COMMENT_STREAM_MS = 1500;

export function StreamedComment({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [shown, setShown] = useState('');

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(text);
      return;
    }

    let frame = 0;
    let start = 0;
    let started = false;

    function tick(now: number) {
      if (start === 0) start = now;
      const t = Math.min(1, (now - start) / COMMENT_STREAM_MS);
      setShown(text.slice(0, Math.round(t * text.length)));
      if (t < 1) frame = requestAnimationFrame(tick);
    }

    function onIntersect(entries: IntersectionObserverEntry[]) {
      if (!entries[0]?.isIntersecting || started) return;
      started = true;
      observer.disconnect();
      frame = requestAnimationFrame(tick);
    }

    const observer = new IntersectionObserver(onIntersect, {
      threshold: 0.4,
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [text]);

  return (
    <p
      ref={ref}
      className={css({
        position: 'relative',
        margin: 0,
        marginTop: '1.5',
        fontSize: 'xs',
        lineHeight: 'snug',
        color: 'text.secondary',
      })}
    >
      <span
        className={css({
          visibility: 'hidden',
        })}
      >
        {text}
      </span>
      <span
        className={css({
          position: 'absolute',
          inset: 0,
        })}
      >
        {shown}
      </span>
    </p>
  );
}
